/**
 * Unicode Braille (U+2800–U+28FF) as a 2x4 sub-cell bitmap.
 *
 * Dot numbering and the bit each dot occupies:
 *
 *   col 0  col 1        bit0 bit3
 *     1      4          bit1 bit4
 *     2      5          bit2 bit5
 *     3      6          bit6 bit7
 *     7      8
 *
 * Drawing in this space gives 2x horizontal and 4x vertical resolution over
 * plain cells, which is what makes sparklines, curves and image conversion
 * legible in a terminal.
 */

export const BRAILLE_BASE = 0x2800;
export const BRAILLE_COLS = 2;
export const BRAILLE_ROWS = 4;

/** BIT[row][col] -> bit value within the Braille pattern byte. */
export const BRAILLE_BIT: readonly (readonly number[])[] = [
  [0x01, 0x08],
  [0x02, 0x10],
  [0x04, 0x20],
  [0x40, 0x80],
];

export function isBraille(ch: string): boolean {
  if (ch.length === 0) return false;
  const cp = ch.codePointAt(0) ?? 0;
  return cp >= BRAILLE_BASE && cp <= BRAILLE_BASE + 0xff;
}

export function brailleFromMask(mask: number): string {
  return String.fromCodePoint(BRAILLE_BASE + (mask & 0xff));
}

export function maskFromBraille(ch: string): number {
  return isBraille(ch) ? ((ch.codePointAt(0) as number) - BRAILLE_BASE) & 0xff : 0;
}

export function dotBit(col: number, row: number): number {
  if (col < 0 || col >= BRAILLE_COLS || row < 0 || row >= BRAILLE_ROWS) return 0;
  return BRAILLE_BIT[row][col];
}

export function setDot(mask: number, col: number, row: number, on: boolean): number {
  const bit = dotBit(col, row);
  if (bit === 0) return mask;
  return on ? mask | bit : mask & ~bit;
}

export function getDot(mask: number, col: number, row: number): boolean {
  const bit = dotBit(col, row);
  return bit !== 0 && (mask & bit) !== 0;
}

/**
 * A dot-addressable surface covering a rectangular block of terminal cells.
 * Construct it from the existing canvas so edits are additive, draw into it in
 * dot coordinates, then read the changed cells back out.
 */
export class BrailleSurface {
  /** Width/height measured in dots. */
  readonly dotWidth: number;
  readonly dotHeight: number;
  private readonly dots: Uint8Array;
  private readonly initial: Uint8Array;

  constructor(
    readonly cols: number,
    readonly rows: number,
    readonly originX = 0,
    readonly originY = 0,
  ) {
    this.dotWidth = cols * BRAILLE_COLS;
    this.dotHeight = rows * BRAILLE_ROWS;
    this.dots = new Uint8Array(this.dotWidth * this.dotHeight);
    this.initial = new Uint8Array(this.dotWidth * this.dotHeight);
  }

  /** Seeds the surface from existing Braille glyphs on the canvas. */
  loadFrom(readChar: (x: number, y: number) => string): void {
    for (let cy = 0; cy < this.rows; cy++) {
      for (let cx = 0; cx < this.cols; cx++) {
        const mask = maskFromBraille(readChar(this.originX + cx, this.originY + cy));
        if (mask === 0) continue;
        for (let r = 0; r < BRAILLE_ROWS; r++) {
          for (let c = 0; c < BRAILLE_COLS; c++) {
            if (getDot(mask, c, r)) {
              const i = (cy * BRAILLE_ROWS + r) * this.dotWidth + cx * BRAILLE_COLS + c;
              this.dots[i] = 1;
              this.initial[i] = 1;
            }
          }
        }
      }
    }
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.dotWidth && y < this.dotHeight;
  }

  set(x: number, y: number, on = true): void {
    if (!this.inBounds(x, y)) return;
    this.dots[y * this.dotWidth + x] = on ? 1 : 0;
  }

  get(x: number, y: number): boolean {
    return this.inBounds(x, y) && this.dots[y * this.dotWidth + x] === 1;
  }

  clear(): void {
    this.dots.fill(0);
  }

  /** Cells whose Braille glyph differs from what was loaded. */
  changedCells(): Array<{ x: number; y: number; char: string; mask: number }> {
    const out: Array<{ x: number; y: number; char: string; mask: number }> = [];
    for (let cy = 0; cy < this.rows; cy++) {
      for (let cx = 0; cx < this.cols; cx++) {
        let mask = 0;
        let before = 0;
        for (let r = 0; r < BRAILLE_ROWS; r++) {
          for (let c = 0; c < BRAILLE_COLS; c++) {
            const i = (cy * BRAILLE_ROWS + r) * this.dotWidth + cx * BRAILLE_COLS + c;
            if (this.dots[i]) mask |= BRAILLE_BIT[r][c];
            if (this.initial[i]) before |= BRAILLE_BIT[r][c];
          }
        }
        if (mask !== before) {
          out.push({ x: this.originX + cx, y: this.originY + cy, char: brailleFromMask(mask), mask });
        }
      }
    }
    return out;
  }

  /** Every cell of the surface, regardless of change (used by image import). */
  allCells(): Array<{ x: number; y: number; char: string; mask: number }> {
    const out: Array<{ x: number; y: number; char: string; mask: number }> = [];
    for (let cy = 0; cy < this.rows; cy++) {
      for (let cx = 0; cx < this.cols; cx++) {
        let mask = 0;
        for (let r = 0; r < BRAILLE_ROWS; r++) {
          for (let c = 0; c < BRAILLE_COLS; c++) {
            const i = (cy * BRAILLE_ROWS + r) * this.dotWidth + cx * BRAILLE_COLS + c;
            if (this.dots[i]) mask |= BRAILLE_BIT[r][c];
          }
        }
        out.push({ x: this.originX + cx, y: this.originY + cy, char: brailleFromMask(mask), mask });
      }
    }
    return out;
  }
}

/** Cell coordinate + dot offset for a pointer position inside a cell. */
export function dotAt(cellX: number, cellY: number, fracX: number, fracY: number): { dx: number; dy: number } {
  const col = Math.min(BRAILLE_COLS - 1, Math.max(0, Math.floor(fracX * BRAILLE_COLS)));
  const row = Math.min(BRAILLE_ROWS - 1, Math.max(0, Math.floor(fracY * BRAILLE_ROWS)));
  return { dx: cellX * BRAILLE_COLS + col, dy: cellY * BRAILLE_ROWS + row };
}
